import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from './Header';
import png1 from '../assets/hardware/pngs/1.png';
import png2 from '../assets/hardware/pngs/2.png';
import png3 from '../assets/hardware/pngs/3.png';
import png4 from '../assets/hardware/pngs/4.png';
import png5 from '../assets/hardware/pngs/5.png';
import png6 from '../assets/hardware/pngs/6.png';
import png7 from '../assets/hardware/pngs/7.png';
import png8 from '../assets/hardware/pngs/8.png';
import { Helmet } from 'react-helmet-async';

const containerVariants = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } },
  exit: { opacity: 0, y: -30, transition: { duration: 0.7, ease: 'easeInOut' } },
};

const imageVariants = {
  initial: (direction) => ({ x: direction > -300 ? 300 : 0, opacity: 0 }),
  animate: { x: 0, opacity: 1, transition: { duration: 0.6, ease: 'easeOut' } },
  exit: (direction) => ({ x: direction > 0 ? -300 : 300, opacity: 0, transition: { duration: 0.6, ease: 'easeInOut' } }),
};




const HalfPageSection = ({ images, title, content }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const timerRef = useRef(null);

  const startAutoSlide = () => {
    timerRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 4000);
  };

  useEffect(() => {
    startAutoSlide();
    return () => clearInterval(timerRef.current);
  }, [images.length]);

  const handleManualChange = (index) => {
    clearInterval(timerRef.current);
    setCurrentIndex(index);
    startAutoSlide();
  };

  return (
    <div className="relative py-16 px-4 lg:px-20 bg-white overflow-hidden">
      <div className="flex flex-col lg:flex-row items-center justify-between gap-12">

        {/* Image Section */}
        <div className="relative w-full h-[370px] lg:w-5/12 flex justify-center items-center">
          <AnimatePresence mode="wait">
       <motion.img
  key={currentIndex}
  src={images[currentIndex].src}
  alt={images[currentIndex].alt}
  title={images[currentIndex].alt}
  variants={imageVariants}
  initial="initial"
  animate="animate"
  exit="exit"
  className="max-w-[320px] pb-8 md:max-w-[400px] object-contain"
  custom={1}
/>

          </AnimatePresence>

          <div className="absolute bottom-0 flex space-x-2">
            {images.map((_, index) => (
              <div
                key={index}
                onClick={() => handleManualChange(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${index === currentIndex ? 'bg-[#FF5300] scale-125' : 'bg-gray-300'} cursor-pointer`}
              />
            ))}
          </div>
        </div>

        {/* Text Section - stays static */}
        <div className="w-full lg:w-7/12 text-start">
          <h2 className="text-2xl md:text-3xl font-bold text-black poppins-font mb-4">
            {title}
            <hr className="w-14 bg-black h-[2px] border-none shadow-lg mt-4" />
          </h2>

          <p className="text-sm md:text-base text-gray-500 poppins-font2 leading-relaxed">
            {content}
          </p>
        </div>

      </div>
    </div>
  );
};




const Hardware = () => {
 const images1 = [
  { src: png1, alt: 'Bill Acceptor Model 1' },
  { src: png2, alt: 'Bill Acceptor Model 2' },
  { src: png3, alt: 'Bill Acceptor Model 3' },
];

const images2 = [
  { src: png4, alt: 'Printer Model 1' },
  { src: png5, alt: 'Printer Model 2' },
  { src: png6, alt: 'Printer Model 3' },
];

const images3 = [
  { src: png7, alt: 'Quixant Board Variant 1' },
  { src: png8, alt: 'Quixant Board Variant 2' },
];


  const title1 = 'Bill Acceptors';
  const content1 = `Bill acceptors are a fundamental part of modern slot machines, offering players a seamless way to insert paper currency. Equipped with advanced sensors, they accurately verify the authenticity of bills and instantly convert their value into playable credits displayed on the screen. By eliminating the need for coins or tokens, bill acceptors reduce maintenance costs and contribute to a more convenient and cashless gaming experience.`;

  const title2 = 'Printers';
  const content2 = `Slot machine printers support cashless gaming by generating ticket-out vouchers for player winnings. Instead of dispensing coins or tokens, the printer issues a printed voucher that can be redeemed at kiosks or cash desks. This not only streamlines casino operations but also lowers maintenance costs related to coin handling. Additionally, printers provide transaction receipts for auditing and player tracking, ensuring regulatory compliance.`;

  const title3 = 'Quixant Board';
  const content3 = `The Quixant board serves as the brain of the slot machine, controlling all essential functions. From managing the Random Number Generator (RNG) to operating the display, audio, and payout mechanisms, it guarantees fair play and secure operation. These boards also store game software, track player interactions, and communicate with casino networks for real-time monitoring and data reporting.`;

  return (
    <>
      <Helmet>
        <title>Premium Slot Game Hardware Services | Fusion Spin</title>
        <meta name="description" content="Explore Fusion Spin's advanced hardware solutions for slot game development and outsourcing. Boost performance with cutting-edge technology." />
        <meta name="keywords" content="Slot Game Hardware, slot game outsourcing, slot game services, casino game hardware, fusion spin, land slot services, custom slot machines, casino game development" />
      </Helmet>

      <Header />

      <section className="pt-12 bg-white px-4 md:px-16 lg:px-28 text-center lg:text-start mx-auto pb-8">
        <h1 className="text-2xl md:text-4xl font-extrabold py-4">
          <span className="poppins-font">Slot Hardware Solutions by Fusion Spin</span>
        </h1>
        <p className="md:text-base text-gray-500 font-medium text-sm poppins-font2 tracking-wider lg:leading-[1.9]  lg:text-start">
          At Fusion Spin, we understand that reliable and efficient hardware is at the core of every successful slot gaming operation. Our comprehensive range of slot machine hardware solutions is designed to enhance player experience, streamline operations, and ensure the utmost reliability. Whether you are looking to upgrade existing machines or build new ones, our advanced hardware components deliver exceptional performance.
        </p>
      </section>

      <HalfPageSection images={images1} title={title1} content={content1} />
      <HalfPageSection images={images2} title={title2} content={content2} />
      <HalfPageSection images={images3} title={title3} content={content3} />

      <section className="pt-12 bg-white px-4 md:px-16 lg:px-28 text-center lg:text-start mx-auto pb-20">
        <h2 className="text-2xl md:text-4xl font-extrabold py-4">
          <span className="poppins-font">Why Choose Fusion Spin Hardware?</span>
        </h2>
        <p className="md:text-base text-gray-500 font-medium text-sm poppins-font2 tracking-wider lg:leading-[1.9] lg:text-start">
          Our slot hardware components are engineered to meet the demands of modern casinos, offering robust performance, low maintenance, and adaptability. Partner with Fusion Spin to elevate your gaming environment with state-of-the-art hardware solutions designed to maximize player satisfaction and operational efficiency.
        </p>
      </section>
    </>
  );
};

export default Hardware;
